<template>
  <div v-if="form" class="request_editor__wrapper">
    <form novalidate @submit.prevent="submit">
      <dialog-modal
        :show-dialog="showCancelReturnDialog"
        @close="onCancelReturn(false)"
        content-ref="cancel-return-dialog"
      >
        <template v-slot:title>
          <c-text as="span" data-cy="cancel-return-dialog-title">
            {{ $t("productReturn.common.dialogs.cancelReturnTitle") }}
          </c-text>
        </template>
        <template v-slot:content>
          <c-text>{{ $t("productReturn.common.dialogs.cancelReturnBody") }}</c-text>
        </template>
        <template v-slot:actions>
          <c-button
            variation="solid"
            @click="onCancelReturn(true)"
            background="brand.beige"
            borderColor="brand.beige"
            mr="md"
            data-cy="cancel-return-dialog-approve"
          >
            {{ $t("common.yesButton") }}
          </c-button>
          <c-button @click="onCancelReturn(false)" data-cy="cancel-return-dialog-reject">
            {{ $t("common.noButton") }}
          </c-button>
        </template>
      </dialog-modal>

      <Wizard
        ref="wizard"
        :validated="true"
        :submit-callback="submit"
        :back-button-title="String($t('common.previous'))"
        :next-button-title="String($t('productReturn.common.nextButtonTitle'))"
        :submit-button-title="String($t('productReturn.common.submitButtonTitle'))"
        :reset-button-title="String($t('common.cancelAndReset'))"
        v-chakra
        mt="3xl"
        @onSubmitError="onSubmitError($event)"
        @onReset="onShowCancelReturnDialog()"
        :currentStep="currentStep"
      >
        <template v-slot:tabs>
          <return-tab-container
            v-if="$refs.wizard && $refs.wizard.pages"
            :currentStep="currentStep"
            :tabData="$refs.wizard.pages"
            :currentPageTitle="currentPageTitle"
          />
        </template>

        <template v-slot:content>
          <Page
            v-show="currentStep === 1"
            :title="String($t('productReturnV2.returnType.menuTitle'))"
            :step="1"
            name="type-selection"
            :preventNavigation="formTypeIsNone()"
            @addPage="addPage"
            @removePage="removePage"
          >
            <c-heading as="h2" :mt="{ sm: 'xl' }" data-cy="return-tab-page-heading">
              1. {{ $t("productReturnV2.returnType.menuTitle") }}
            </c-heading>
            <RMATypeSelection :form="form" @onSelect="onTypeSelect" />
          </Page>

          <template v-if="formTypeIsNone()">
            <!-- these work as placeholders when form type hasn't been selected yet -->
            <Page
              v-show="currentStep === 2"
              :step="2"
              :title="String($t('productReturn.contactDetails.menuTitle'))"
              name="details"
              @addPage="addPage"
              @removePage="removePage"
              :disabled="true"
            />
            <Page
              v-show="currentStep === 3"
              :step="3"
              :title="String($t('productReturnV2.common.productsToReturn'))"
              name="products"
              @addPage="addPage"
              @removePage="removePage"
              :disabled="true"
            />
            <Page
              v-show="currentStep === 4"
              :step="4"
              :title="String($t('productReturnV2.returnDetails.title'))"
              name="return_details"
              @addPage="addPage"
              @removePage="removePage"
              :disabled="true"
            />
            <Page
              v-show="currentStep === 5"
              :step="5"
              :title="String($t('productReturn.summary.menuTitle'))"
              name="summary"
              @addPage="addPage"
              @removePage="removePage"
              :disabled="true"
            />
          </template>

          <c-box v-else-if="formTypeIsMaintenanceReturn()">
            <!-- Contact details -->
            <Page
              v-show="currentStep === 2"
              :step="2"
              :validation-promise="validateAddress"
              :validation-params="[
                form.contactDetails.partnerZipCode,
                form.contactDetails.partnerCountry,
                form.contactDetails.partnerCity,
              ]"
              :title="String($t('productReturn.contactDetails.menuTitle'))"
              :errors="hasErrors('details')"
              name="details"
              @addPage="addPage"
              @removePage="removePage"
            >
              <c-heading as="h2" :mt="{ sm: 'xl' }" data-cy="return-tab-page-heading">
                2. {{ $t("productReturn.contactDetails.menuTitle") }}
              </c-heading>
              <RMA01SenderDetails
                v-model="form.contactDetails"
                :return-type="form.returnType"
                :address-validation-api="addressValidationApi"
                :shipper="form.shipperType"
                @errors="onError('details', $event)"
                @addressSettled="onAddressSettled"
              />
            </Page>

            <!-- Maintenance return: Products to return -->
            <Page
              v-show="currentStep === 3"
              :step="3"
              :title="String($t('productReturn.maintenanceRequest.menuTitle'))"
              :errors="hasErrors('products')"
              name="products"
              @addPage="addPage"
              @removePage="removePage"
            >
              <c-heading as="h2" :mt="{ sm: 'xl' }" data-cy="return-tab-page-heading">
                3. {{ $t("productReturn.maintenanceRequest.menuTitle") }}
              </c-heading>
              <RMA02CRProducts
                v-model="form.productDetails"
                :pro-forma-required="isProFormaRequired()"
                @errors="onError('products', $event)"
              />
            </Page>

            <!-- Maintenance return: Return details -->
            <Page
              v-show="currentStep === 4"
              :step="4"
              :title="String($t('productReturnV2.returnDetails.title'))"
              :submit-page="true"
              :errors="hasErrors('return_details')"
              name="return_details"
              @addPage="addPage"
              @removePage="removePage"
            >
              <c-heading as="h2" :mt="{ sm: 'xl' }" data-cy="return-tab-page-heading">
                4. {{ $t("productReturnV2.returnDetails.title") }}
              </c-heading>
              <RMA03Details
                v-if="currentStep === 4"
                v-model="form.returnDetails"
                :total-product-count="totalProductCount"
                :shipper="form.shipperType"
                :country="form.contactDetails.partnerCountry"
                @errors="onError('return_details', $event)"
                :is-maintenance-return="true"
              />
            </Page>

            <!-- Shipping -->
            <Page
              v-show="currentStep === 5"
              :step="5"
              :title="String($t('productReturn.summary.menuTitle'))"
              name="summary"
              :final-page="true"
              @addPage="addPage"
              @removePage="removePage"
            >
              <c-heading as="h2" :mt="{ sm: 'xl' }" data-cy="return-tab-page-heading">
                5. {{ $t("productReturn.summary.menuTitle") }}
              </c-heading>
              <RMA04Completed
                :rma-id="form.rmaId"
                :has-parcel="form.hasParcel"
                :pro-forma-required="isProFormaRequired()"
                :shipper="form.shipperType"
                :planned-shipping-date="plannedDHLShippingDate"
                :zip-code="form.contactDetails.partnerZipCode"
                :country-code="form.contactDetails.partnerCountry"
                @onDownloadReturnLabel="onDownloadParcelLabelAction"
                @onDownloadInternationalForm="onDownloadInternationalFormAction"
                @onGetDetailedView="onGetDetailedView"
              />
            </Page>
          </c-box>

          <c-box v-else-if="formTypeIsSalesReturn()">
            <!-- Select order -->
            <Page
              v-show="currentStep === 2"
              :step="2"
              :title="String($t('productReturn.selectOrder.menuTitle'))"
              :errors="hasErrors('orders')"
              name="orders"
              @addPage="addPage"
              @removePage="removePage"
            >
              <c-heading as="h2" :mt="{ sm: 'xl' }" data-cy="return-tab-page-heading">
                2. {{ $t("productReturnV2.selectOrder.title") }}
              </c-heading>
              <RMA00SRSelectOrderForReturn v-model="form" />
            </Page>

            <!-- Contact details -->
            <Page
              v-show="currentStep === 3"
              :step="3"
              :validation-promise="validateAddress"
              :validation-params="[
                form.contactDetails.partnerZipCode,
                form.contactDetails.partnerCountry,
                form.contactDetails.partnerCity,
              ]"
              :title="String($t('productReturn.contactDetails.menuTitle'))"
              :errors="hasErrors('details')"
              name="details"
              @addPage="addPage"
              @removePage="removePage"
            >
              <c-heading as="h2" :mt="{ sm: 'xl' }" data-cy="return-tab-page-heading">
                3. {{ $t("productReturn.contactDetails.menuTitle") }}
              </c-heading>
              <RMA01SenderDetails
                v-model="form.contactDetails"
                :return-type="form.returnType"
                :address-validation-api="addressValidationApi"
                :shipper="form.shipperType"
                @errors="onError('details', $event)"
                @addressSettled="onAddressSettled"
              />
            </Page>

            <!-- Products to return -->
            <Page
              v-show="currentStep === 4"
              :step="4"
              :title="String($t('productReturn.salesReturn.menuTitle'))"
              :errors="hasErrors('products')"
              name="products"
              @addPage="addPage"
              @removePage="removePage"
            >
              <c-heading as="h2" :mt="{ sm: 'xl' }" data-cy="return-tab-page-heading">
                4. {{ $t("productReturnV2.salesReturn.productsToReturn.title") }}
              </c-heading>
              <RMA02SRProducts
                v-model="form.productDetails"
                :pro-forma-required="isProFormaRequired()"
                @errors="onError('products', $event)"
              >
              </RMA02SRProducts>
            </Page>

            <!-- Return details -->
            <Page
              v-show="currentStep === 5"
              :step="5"
              :title="String($t('productReturnV2.returnDetails.title'))"
              :submit-page="true"
              :errors="hasErrors('return_details')"
              name="return_details"
              @addPage="addPage"
              @removePage="removePage"
            >
              <c-heading as="h2" :mt="{ sm: 'xl' }" data-cy="return-tab-page-heading">
                5. {{ $t("productReturnV2.returnDetails.title") }}
              </c-heading>
              <RMA03Details
                v-if="currentStep === 5"
                v-model="form.returnDetails"
                :total-product-count="totalProductCount"
                :shipper="form.shipperType"
                :country="form.contactDetails.partnerCountry"
                @errors="onError('return_details', $event)"
                :is-sales-return="true"
              />
            </Page>

            <!-- Shipping -->
            <Page
              v-show="currentStep === 6"
              :step="6"
              :final-page="true"
              :title="String($t('productReturn.summary.menuTitle'))"
              name="summary"
              @addPage="addPage"
              @removePage="removePage"
            >
              <c-heading as="h2" :mt="{ sm: 'xl' }" data-cy="return-tab-page-heading">
                6. {{ $t("productReturn.summary.menuTitle") }}
              </c-heading>
              <RMA04Completed
                :rma-id="form.rmaId"
                :has-parcel="form.hasParcel"
                :pro-forma-required="isProFormaRequired()"
                :shipper="form.shipperType"
                :planned-shipping-date="plannedDHLShippingDate"
                :zip-code="form.contactDetails.partnerZipCode"
                :country-code="form.contactDetails.partnerCountry"
                @onDownloadReturnLabel="onDownloadParcelLabelAction"
                @onDownloadInternationalForm="onDownloadInternationalFormAction"
                @onGetDetailedView="onGetDetailedView"
              >
              </RMA04Completed>
            </Page>
          </c-box>

          <c-box v-else-if="formTypeIsCampaignReturn()">
            <!-- Contact details -->
            <Page
              v-show="currentStep === 2"
              :step="2"
              :validation-promise="validateAddress"
              :validation-params="[
                form.contactDetails.partnerZipCode,
                form.contactDetails.partnerCountry,
                form.contactDetails.partnerCity,
              ]"
              :title="String($t('productReturn.contactDetails.menuTitle'))"
              :errors="hasErrors('details')"
              name="details"
              @addPage="addPage"
              @removePage="removePage"
            >
              <c-heading as="h2" :mt="{ sm: 'xl' }" data-cy="return-tab-page-heading">
                2. {{ $t("productReturn.contactDetails.menuTitle") }}
              </c-heading>
              <RMA01SenderDetails
                v-model="form.contactDetails"
                :return-type="form.returnType"
                :address-validation-api="addressValidationApi"
                :shipper="form.shipperType"
                @errors="onError('details', $event)"
                @addressSettled="onAddressSettled"
              />
            </Page>

            <!-- Products to return -->
            <Page
              v-show="currentStep === 3"
              :step="3"
              :title="String($t('productReturn.campaignReturn.menuTitle'))"
              :errors="hasErrors('products')"
              name="products"
              @addPage="addPage"
              @removePage="removePage"
            >
              <c-heading as="h2" :mt="{ sm: 'xl' }" data-cy="return-tab-page-heading">
                3. {{ $t("productReturnV2.campaignReturn.title") }}
              </c-heading>
              <RMA02CampaignReturnProducts
                v-model="form.productDetails"
                :pro-forma-required="isProFormaRequired()"
                @errors="onError('products', $event)"
              />
            </Page>

            <!-- Return details -->
            <Page
              v-show="currentStep === 4"
              :step="4"
              :title="String($t('productReturnV2.returnDetails.title'))"
              :submit-page="true"
              :errors="hasErrors('return_details')"
              name="return_details"
              @addPage="addPage"
              @removePage="removePage"
            >
              <c-heading as="h2" :mt="{ sm: 'xl' }" data-cy="return-tab-page-heading">
                4. {{ $t("productReturnV2.returnDetails.title") }}
              </c-heading>
              <RMA03Details
                v-if="currentStep === 4"
                v-model="form.returnDetails"
                :total-product-count="totalProductCount"
                :shipper="form.shipperType"
                :country="form.contactDetails.partnerCountry"
                @errors="onError('return_details', $event)"
                :is-campaign-return="true"
              />
            </Page>

            <!-- Shipping -->
            <Page
              v-show="currentStep === 5"
              :step="5"
              :title="String($t('productReturn.summary.menuTitle'))"
              :final-page="true"
              name="summary"
              @addPage="addPage"
              @removePage="removePage"
            >
              <c-heading as="h2" :mt="{ sm: 'xl' }" data-cy="return-tab-page-heading">
                5. {{ $t("productReturn.summary.menuTitle") }}
              </c-heading>
              <RMA04Completed
                :rma-id="form.rmaId"
                :has-parcel="form.hasParcel"
                :pro-forma-required="isProFormaRequired()"
                :shipper="form.shipperType"
                :planned-shipping-date="plannedDHLShippingDate"
                :zip-code="form.contactDetails.partnerZipCode"
                :country-code="form.contactDetails.partnerCountry"
                @onDownloadReturnLabel="onDownloadParcelLabelAction"
                @onDownloadInternationalForm="onDownloadInternationalFormAction"
                @onGetDetailedView="onGetDetailedView"
              >
              </RMA04Completed>
            </Page>
          </c-box>
        </template>
      </Wizard>
    </form>
  </div>
</template>

<script lang="ts">
import Vue, { defineComponent } from "vue";
import { mapState } from "vuex";
import deepmerge from "deepmerge";
import LocaleChanger from "@/components/LocaleChanger.vue";
import WizardForm from "@/components/WizardForm.vue";
import WizardPage from "@/components/WizardPage.vue";
import RMA00SRSelectOrderForReturn from "@/views/rma/RMA00SRSelectOrderForReturn.vue";
import RMA01SenderDetails from "@/views/rma/RMA01SenderDetails.vue";
import RMA02CRProducts from "@/views/rma/RMA02CRProducts.vue";
import RMA02SRProducts from "@/views/rma/RMA02SRProducts.vue";
import RMA02CampaignReturnProducts from "@/views/rma/RMA02CampaignReturnProducts.vue";
import RMA03Details from "@/views/rma/RMA03Details.vue";
import RMA04Completed from "@/views/rma/RMA04Completed.vue";
import {
  BlockableActivity,
  CorePermission,
  currentStep,
  IMaintenanceReturnResult,
  IOrder,
  IPartnerInfo,
  IResultEntity,
  ReturnType,
  ShipperType,
  SubsidiaryIds,
  IRootState,
} from "@iloq/shared";
import { getPhoneNumberInfo } from "@/util/PhoneNumber";
import AddressValidationApi from "../../api/AddressValidationApi";
import config from "@/config";
import Modal from "@/components/Modal.vue";
import { handleCommonApiErrors } from "@/api/CommonApiErrorHandler";
import { omit } from "lodash";
import Button from "@/design-system/elements/Button.vue";
import Heading from "@/design-system/elements/Heading.vue";
import Icon from "@/design-system/elements/Icon.vue";
import { formMixin, productReturnDataMixin } from "./RMAMixins";
import RMATypeSelection from "./RMATypeSelection.vue";
import { CBox, CButton, CHeading, CText } from "@chakra-ui/vue";
import ReturnTabContainer from "@/components/returns/ReturnTabContainer.vue";
import momentTz from "moment-timezone";

export default defineComponent({
  name: "RMARequestEditor",
  mixins: [productReturnDataMixin, formMixin],
  components: {
    LocaleChanger,
    RMATypeSelection,
    RMA00SRSelectOrderForReturn,
    RMA01SenderDetails,
    RMA02CRProducts,
    RMA02SRProducts,
    RMA02CampaignReturnProducts,
    RMA03Details,
    RMA04Completed,
    Wizard: WizardForm,
    Page: WizardPage,
    Button,
    Heading,
    Icon,
    "dialog-modal": Modal,
    CButton,
    CText,
    CHeading,
    CBox,
    ReturnTabContainer,
  },
  data() {
    return {
      moment: this.$moment,
      loaded: false,
      isError: false,
      errors: {} as any,
      errorHeader: "common.error.invalidFields",
      form: {} as any,
      pageErrors: [] as any[],
      showAllReturns: false,
      showCancelReturnDialog: false,
      addressValidationApi: new AddressValidationApi(),
    };
  },
  computed: {
    ...mapState({
      user: (state: unknown) => (state as IRootState).user!,
      partnerInfo: (state: unknown) => (state as any).partnerInfo.partnerInfo as IPartnerInfo,
      returnListing: (state: unknown) => (state as any).returns.list as any[],
      ordersApplicableForSalesReturn: (state: unknown) =>
        (state as any).orders.ordersApplicableForSalesReturns as IOrder[],
    }),
    currentStep(): number {
      const queryStep = this.$route.query?.step;
      const maximum = (this.$refs.wizard as any)?.pages?.length;
      return currentStep(queryStep as string, maximum);
    },
    currentPageTitle(): string {
      return (this.$refs.wizard as any)?.currentPageTitle();
    },
    plannedDHLShippingDate(): string {
      const date = this.form?.returnDetails?.plannedShippingDate;

      if (!date) {
        return "";
      }

      return momentTz(date).format("D.M.YYYY");
    },
    isSubmitted(): boolean {
      return !!this.form && this.form.submitted;
    },
    showCampaignReturn(): boolean {
      const { country } = this.partnerInfo;
      const campaignReturnAllowedCountryList: string[] = config.get(
        "campaignReturnAllowedCountryList",
      );

      return campaignReturnAllowedCountryList.includes(country);
    },
    totalProductCount(): number {
      let total = 0;
      if (this.form.returnType !== ReturnType.NONE) {
        const details = this.form.productDetails;
        if (this.form.returnType === ReturnType.MAINTENANCE_RETURN) {
          for (let siteIdx = 0; siteIdx < details.sites.length; siteIdx += 1) {
            const site: any = details.sites[siteIdx];
            const productCount = (
              site.products.map(({ count }: any) => count ?? 0) as number[]
            ).reduce((prev, curr) => prev + curr, 0);
            for (let productIdx = 0; productIdx !== productCount; productIdx += 1) {
              const product: any = site.products[productIdx];
              if (product && product.count) {
                total += Number(product.count);
              }
            }
          }
        } else if (this.form.returnType === ReturnType.SALES_RETURN) {
          for (let productIdx = 0; productIdx < details.productCount; productIdx += 1) {
            const product: any = details.products[productIdx];
            if (product && product.returnCount) {
              total += Number(product.returnCount);
            }
          }
        } else if (this.form.returnType === ReturnType.CAMPAIGN_RETURN) {
          total = details.campaignReturnKeys ? (total += Number(details.campaignReturnKeys)) : 0;
          total = details.campaignReturnLocksAndCylinders
            ? (total += Number(details.campaignReturnLocksAndCylinders))
            : total;
          total = details.campaignReturnOnlineProducts
            ? (total += Number(details.campaignReturnOnlineProducts))
            : total;
        }
      }
      return total;
    },
    completedReturnId(): string {
      return this.isMaintenanceReturn(this.form) ? this.form.id : this.form.rmaId;
    },
    shouldBeValidatedWithDHLEndpoint(): boolean {
      // Use DHL always if enabled (because shipperType can be stale if changed between UPS & DHL locations)
      return this.$store?.state?.features?.useDHLInAddressValidation;
    },
  },
  methods: {
    addPage(ref: any): void {
      this.$nextTick(() => {
        (this.$refs.wizard as any)?.addPage(ref);
      });
    },
    removePage(ref: any): void {
      (this.$refs.wizard as any)?.removePage(ref);
    },
    nextPage(): void {
      this.$nextTick(() => {
        (this.$refs.wizard as any)?.nextPage();
      });
    },
    isProFormaRequired(): boolean {
      const country = this.form.contactDetails.partnerCountry;
      const subsidiaryId: SubsidiaryIds =
        (this.user.subsidiaryId as SubsidiaryIds) ?? SubsidiaryIds.FINLAND;
      const nonProFormaCountries: string[] = config.get(
        "proFormaNotRequiredCountryListBySubsidiary",
      )[subsidiaryId];
      return !nonProFormaCountries.includes(country);
    },
    onShowCancelReturnDialog(): void {
      this.showCancelReturnDialog = true;
    },
    onCancelReturn(success: boolean): void {
      this.showCancelReturnDialog = false;
      if (success) {
        this.$router.push({
          path: "/support/returns",
        });
      }
    },
    onGetDetailedView(): void {
      const formData = {
        productReturnData: this.form,
      } as IResultEntity;
      const rmaId = this.form.rmaId;

      if (this.isSalesReturn(formData)) {
        this.$router.push({
          path: `/support/returns/sales_return/${rmaId}`,
        });
      } else if (this.isMaintenanceReturn(formData)) {
        this.$router.push({
          path: `/support/returns/maintenance_return/${rmaId}`,
        });
      } else if (this.isCampaignReturn(formData)) {
        this.$router.push({
          path: `/support/returns/campaign_return/${rmaId}`,
        });
      } else {
        this.$router.push({
          path: `/support/returns/old_return/${rmaId}`,
        });
      }
    },
    async submit(): Promise<void> {
      const formWithoutPhoneNumberInput = omit(this.form, "contactDetails.phoneNumberInputValue");
      const cleanedForm = omit(formWithoutPhoneNumberInput, "contactDetails.cities");
      const returnWithUser = { productReturn: cleanedForm, email: this.user.email };
      const data = await this.$store.dispatch("returns/createProductReturn", returnWithUser);
      const hasParcel = this.hasUpsParcel(data) || this.hasPostiParcel(data);

      Vue.set(this.form, "submitted", true);
      Vue.set(this.form, "id", data.id);
      Vue.set(this.form, "rmaId", data.rmaId);
      Vue.set(this.form, "hasParcel", hasParcel);
    },
    hasUpsParcel(data: IMaintenanceReturnResult | any): boolean {
      const isUPS =
        data.shipperType === ShipperType.UPS ||
        data.shipmentDetails?.shipperType === ShipperType.UPS;

      if (this.isMaintenanceReturn(data)) {
        return (
          isUPS && (data as IMaintenanceReturnResult).shipmentDetails?.trackingCodes?.length > 0
        );
      }

      return (
        isUPS &&
        data.LabelRecoveryResponse !== undefined &&
        data.LabelRecoveryResponse.ShipmentIdentificationNumber !== undefined
      );
    },
    hasPostiParcel(data: IMaintenanceReturnResult | any) {
      const isPosti =
        data.shipperType === ShipperType.POSTI ||
        data.shipmentDetails?.shipperType === ShipperType.POSTI;

      if (this.isMaintenanceReturn(data)) {
        return (
          isPosti && (data as IMaintenanceReturnResult).shipmentDetails?.trackingCodes?.length > 0
        );
      }

      return isPosti && data.parcels?.length > 0;
    },
    onSubmitError(error: any) {
      if (error.response?.status === 404 && this.form.returnType === ReturnType.SALES_RETURN) {
        this.$store.commit("showGeneralError", {
          dialogTitle: this.$t("dialog.generalError.title"),
          dialogText: this.$t("productReturn.salesReturn.orderAlreadyHasReturnError"),
        });
      } else if (
        error.message?.includes("Serial number already in use") &&
        this.form.returnType === ReturnType.MAINTENANCE_RETURN
      ) {
        this.$store.commit("showGeneralError", {
          dialogTitle: this.$t("dialog.generalError.title"),
          dialogText: this.$t("productReturn.common.error.serialError"),
        });
      } else {
        this.$store.commit("showGeneralError", {
          dialogTitle: this.$t("dialog.generalError.title"),
          dialogText: this.$t("productReturn.common.error.serverError"),
        });
      }
    },
    onDownloadParcelLabelAction() {
      this.onDownloadParcelLabel(this.completedReturnId);
    },
    onDownloadInternationalFormAction() {
      this.onDownloadInternationalForm(this.completedReturnId);
    },
    onDownloadParcelLabel(id: string) {
      const email = this.user.email;
      this.$store.dispatch("fetchDownloadToken").then((downloadToken) => {
        const url = `/api/v1/returns/parcelLabels/${email}/${id}?auth_token=${downloadToken}&return_type=${this.form.returnType}`;
        window.open(url, "_blank");
      });
    },
    onDownloadInternationalForm(id: string) {
      const email = this.user.email;
      this.$store.dispatch("fetchDownloadToken").then((downloadToken) => {
        const url = `/api/v1/returns/internationalForm/${email}/${id}?auth_token=${downloadToken}&return_type=${this.form.returnType}`;
        window.open(url, "_blank");
      });
    },
    onReset(merge: Record<string, unknown>) {
      // Reset page isActivated and isVisited so
      // they don't appear as visited when changing return type
      (this.$refs.wizard as any)?.resetPagesVisitedInfo();

      const country = this.partnerInfo?.country || this.user?.countryCode || "FI";
      const zipCode = this.partnerInfo?.zipCode;
      const city = this.partnerInfo?.city;

      if (!this.form || !this.form.shipperType) {
        if (zipCode && city) {
          this.getShipperForLocation(country, zipCode, city).then((shipperType: ShipperType) => {
            this.mergeDataToForm(shipperType, country, merge);
          });
        } else {
          this.mergeDataToForm(ShipperType.MANUAL, country, merge); // Some valid value needed as "placeholder"
        }
      } else {
        this.mergeDataToForm(this.form.shipperType, country, merge);
      }
    },
    async onTypeSelect(merge: Record<string, unknown>) {
      if (merge.returnType === ReturnType.MAINTENANCE_RETURN) {
        const blocked = await this.$store.dispatch("partnerInfo/handleBlockedActivity", {
          anyOf: [BlockableActivity.SHIPMENTS, BlockableActivity.INVOICING],
        });
        if (blocked) {
          return;
        }
      }

      this.onReset(merge);
      this.$nextTick(() => {
        this.nextPage();
      });
    },
    mergeDataToForm(shipperType: ShipperType, country: string, merge: any) {
      const data = {
        version: "0.1.7",
        returnType: ReturnType.NONE,
        shipperType: shipperType,
        contactDetails: {
          fullName: this.user ? `${this.user.firstName} ${this.user.lastName}` : "",
          emailAddress: this.user?.email || "",
          phoneNumber: this.partnerInfo?.phoneNumber || "",
          phoneNumberInputValue: getPhoneNumberInfo(this.partnerInfo?.phoneNumber || "", country)
            .phoneNumberWithoutCountryCallingCode,
          partnerName: this.partnerInfo?.partnerName || "",
          partnerId: this.partnerInfo?.partnerId || "",
          partnerAddress1: this.partnerInfo?.address || "",
          partnerAddress2: this.partnerInfo?.address2 || "",
          partnerZipCode: this.partnerInfo?.zipCode || "",
          partnerCity: "",
          partnerCountry: country,
          contactPerson: this.partnerInfo?.contactPerson?.name || "",
          cities: [],
        },
        productDetails: {},
        returnDetails: {},
        rmaId: "",
        relatedOrderNumber: "",
      };
      this.form = deepmerge(data, merge || {});
      this.pageErrors = [];
    },
    onError(name: string, errors: boolean) {
      if (this.pageErrors.indexOf(name) >= 0 && !errors) {
        const index = this.pageErrors.indexOf(name);
        if (index >= 0) {
          this.pageErrors.splice(index, 1);
        }
      } else if (this.pageErrors.indexOf(name) < 0 && errors) {
        this.pageErrors.push(name);
      }
    },
    hasErrors(name: string): boolean {
      return this.pageErrors.indexOf(name) >= 0 || false;
    },
    hasOrderPermissions() {
      return this.user.hasPermissions([CorePermission.order]);
    },
    async onAddressSettled() {
      await this.updateShipperType();
    },
    getShipperForLocation(
      countryCode: string,
      zipCode: string,
      city: string,
    ): Promise<ShipperType> {
      return this.$store.dispatch("returns/getShipperType", { countryCode, zipCode, city });
    },
    async updateShipperType() {
      return this.getShipperForLocation(
        this.form.contactDetails.partnerCountry,
        this.form.contactDetails.partnerZipCode,
        this.form.contactDetails.partnerCity,
      ).then((shipperType: ShipperType) => {
        this.form.shipperType = shipperType;
        if (this.form.shipperType === ShipperType.UPS) {
          delete this.form.returnDetails.weight;
          delete this.form.returnDetails.$measurementsPerPackage;
          if (this.form.contactDetails.partnerCountry === "US") {
            this.form.returnDetails.copies = 1;
          }
          if (this.form.returnDetails.copies) {
            this.form.returnDetails.$weightPerPackage = [];
            for (let i = 0; i < this.form.returnDetails.copies; i += 1) {
              this.form.returnDetails.$weightPerPackage.push({ weight: "" });
            }
          }
        } else if ([ShipperType.DHL, ShipperType.CONTACT_CS].includes(this.form.shipperType)) {
          delete this.form.returnDetails.weight;
          if (this.form.returnDetails.copies) {
            this.form.returnDetails.$weightPerPackage = [];
            this.form.returnDetails.$measurementsPerPackage = [];
            for (let i = 0; i < this.form.returnDetails.copies; i += 1) {
              this.form.returnDetails.$weightPerPackage.push({ weight: "" });
              this.form.returnDetails.$measurementsPerPackage.push({
                length: "",
                width: "",
                height: "",
              });
            }
          }
        } else if (this.form.shipperType === ShipperType.POSTI) {
          delete this.form.returnDetails.$weightPerPackage;
          delete this.form.returnDetails.$measurementsPerPackage;
        } else if (this.form.shipperType === ShipperType.MANUAL) {
          delete this.form.returnDetails.weight;
          delete this.form.returnDetails.$weightPerPackage;
          delete this.form.returnDetails.$measurementsPerPackage;
        }
      });
    },
    async validateAddress(zipCode: string, countryCode: string, city: string): Promise<string[]> {
      try {
        const result = await this.addressValidationApi.validateAddressAndGetCities(
          zipCode,
          countryCode,
          this.shouldBeValidatedWithDHLEndpoint,
          false, // Some places (like 85633 Sasabe in US) have no DHL pickup available but delivery works --> validate as delivery address as a workaround
          city,
        );
        return result;
      } catch (error) {
        handleCommonApiErrors(error as Error);
        throw error;
      }
    },
    onErrorsChanged() {
      this.isError = this.errors.length > 0;
    },
    preventNavigatingStepsBackwardsAfterSubmit() {
      if (this.isSubmitted && (this.$refs.wizard as any)?.isLastPage()) {
        this.$router.replace({
          query: {
            step: (this.$refs.wizard as any).getCurrentPage().step,
          },
        });
      }
    },
  },
  watch: {
    "$route.query.step": "preventNavigatingStepsBackwardsAfterSubmit",
    errors: "onErrorsChanged",
  },
  beforeMount() {
    this.onReset({});

    const oldQuery = this.$router.currentRoute.query;
    if (oldQuery && Object.keys(oldQuery).length) {
      // reset step query parameter
      this.$router.push({
        query: {},
      });
    }
  },
  mounted() {
    if (this.hasOrderPermissions()) {
      this.$store.dispatch("orders/fetchOrdersApplicableForSalesReturns");
    }
  },
});
</script>
